<script lang="ts" setup>
/**
 * @name Dashboard.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @Jorel254 - Versión inicial
 */

import { computed, onMounted, ref } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { useStore } from 'vuex'
import { apiService } from '@/services/apiService'
import { getInstitutionCounter } from '@/components/utils/functions'
/**
 * @description Props del componente
 */

const error = ref<string | null>(null)
const submitting = ref(false)
const successMessage = ref<string | null>(null)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const store = useStore()
const currentInstitutionId = ref<number>(0)

let config = ref({
	config: {
		certificates: {
			decimals: 0,
			cycleMask: String.raw`\d{4}-[\dAB]{1,2}`,
			hasAccents: false,
			mayus: false
		},
		degree: {
			conectorCarreraNivel: '',
			hasAccents: false,
			services: {
				id: '',
				url: '',
				user: '',
				pass: ''
			}
		}
	},
	testMode: {
		config: {
			degree: {
				services: {
					id: '',
					url: '',
					user: '',
					pass: ''
				}
			}
		}
	}
})

const selectedInstitution = computed(() => store.state.global.selectedInstitution)

const getConfig = async () => {
	try {
		const response = await apiService.post('getConfigData', {
			institutionId: store.getters['global/currentInstitution']
		})
		const data = response.data.config
		config.value = data
		if (config.value.config.certificates.cycleMask === '') {
			config.value.config.certificates.cycleMask = String.raw`\d{4}-[\dAB]{1,2}`
		}
	} catch (error: any) {
		console.error('Error al obtener config:', error.response?.data || error.message)
	}
}

onMounted(async () => {
	currentInstitutionId.value = await getInstitutionCounter()
	await getConfig()
})

const saveConfig = async () => {
	const institutionId = store.getters['global/currentInstitution']
	try {
		submitting.value = true
		await apiService.post('saveConfiguracion', {
			institutionId: institutionId,
			config: config.value.config,
			testMode: config.value.testMode
		})
		showSuccessModal.value = true
		successMessage.value = 'Configuración actualizada correctamente'
	} catch (err: any) {
		error.value = (err as Error).message || 'Error al actualizar la configuración'
		showErrorModal.value = true
	} finally {
		submitting.value = false
	}
}
</script>

<template>
	<div class="configuracion">
		<VaCard class="bitacora-card">
			<VaCardTitle class="header-gradient">
				<div class="header-content">
					<div class="header-main">
						<div class="header-icon-wrapper">
							<va-icon name="settings" class="header-icon" />
						</div>
						<div class="header-text">
							<h2 class="header-title">Configuración institucional</h2>
							<p class="header-subtitle">Ajusta parámetros de certificados, títulos y servicios MET de la institución.</p>
						</div>
						<va-button class="action-button action-button--save" :loading="submitting" :disabled="submitting" @click="saveConfig()">
							<va-icon name="save" class="button-icon" />
							Guardar cambios
						</va-button>
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="card-content">
				<div class="pe-toolbar">
					<div class="pe-toolbar__meta">
						<span class="pe-chip">Institución #{{ currentInstitutionId || '—' }}</span>
						<span class="pe-chip pe-chip--accent">ID: {{ selectedInstitution?.idNombreInstitucion || 'Sin dato' }}</span>
						<p class="pe-hint">Revisa cada sección y guarda los cambios cuando la configuración esté lista.</p>
					</div>
				</div>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="description" class="pe-section__icon" />
						<h3 class="pe-section__title">Configuración de certificados</h3>
					</div>
					<div class="pe-settings-grid">
						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="pin" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Decimales</h4>
									<p class="pe-panel__desc">Define los decimales para créditos. Ej: 2 → 9.45, 3 → 9.455</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.config.certificates.decimals" placeholder="Cantidad de decimales" class="form-input" :rules="[value => !!value || 'El valor es requerido']" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="theater_comedy" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Máscara de ciclo</h4>
									<p class="pe-panel__desc">4 números, guión y 1-2 caracteres (números, A o B). Ej: 0000-01</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.config.certificates.cycleMask" readonly placeholder="Máscara de ciclo" class="form-input" :rules="[value => !!value || 'La máscara de ciclo es requerida']" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="check_box" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Quitar acentos</h4>
									<p class="pe-panel__desc">Normaliza textos eliminando acentos del alumno y responsable.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaRadio
									v-model="config.config.certificates.hasAccents"
									:options="[
										{ text: 'Mantener acentos', value: true },
										{ text: 'Quitar acentos', value: false }
									]"
									value-by="value" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="text_increase" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Convertir a mayúsculas</h4>
									<p class="pe-panel__desc">Aplica a nombre de carrera, asignatura y campus.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaRadio
									v-model="config.config.certificates.mayus"
									:options="[
										{ text: 'Convertir', value: true },
										{ text: 'Mantener minúsculas', value: false }
									]"
									value-by="value" />
							</div>
						</article>
					</div>
				</section>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="school" class="pe-section__icon" />
						<h3 class="pe-section__title">Configuración de títulos</h3>
					</div>
					<div class="pe-settings-grid pe-settings-grid--two">
						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="account_balance" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Conector carrera-nivel</h4>
									<p class="pe-panel__desc">Preposición que une nivel y carrera. Ej: Maestría EN Derecho</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input
									v-model="config.config.degree.conectorCarreraNivel"
									placeholder="Ej: EN"
									class="form-input"
									:rules="[value => !!value || 'El conector es requerido', value => value.length === 2 || 'Debe tener 2 caracteres']" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="check_box" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Quitar acentos (títulos)</h4>
									<p class="pe-panel__desc">Normaliza textos eliminando acentos en títulos electrónicos.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaRadio
									v-model="config.config.degree.hasAccents"
									:options="[
										{ text: 'Mantener acentos', value: true },
										{ text: 'Quitar acentos', value: false }
									]"
									value-by="value" />
							</div>
						</article>
					</div>
				</section>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="cloud" class="pe-section__icon" />
						<h3 class="pe-section__title">Servicio títulos — producción</h3>
					</div>
					<div class="pe-settings-grid">
						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="pin" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">ID del servicio</h4>
									<p class="pe-panel__desc">Identificador del servicio de títulos MET.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.config.degree.services.id" placeholder="ID del servicio" class="form-input" :rules="[value => !!value || 'El ID es requerido']" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="link" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">URL del servicio</h4>
									<p class="pe-panel__desc">Endpoint de conexión al servicio MET.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.config.degree.services.url" placeholder="URL del servicio" class="form-input" :rules="[value => !!value || 'La URL es requerida']" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="person" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Usuario</h4>
									<p class="pe-panel__desc">Credencial de acceso al servicio MET.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.config.degree.services.user" placeholder="Usuario" class="form-input" :rules="[value => !!value || 'El usuario es requerido']" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="key" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Contraseña</h4>
									<p class="pe-panel__desc">Contraseña de acceso al servicio MET.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.config.degree.services.pass" placeholder="Contraseña" class="form-input" :rules="[value => !!value || 'La contraseña es requerida']" />
							</div>
						</article>
					</div>
				</section>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="science" class="pe-section__icon pe-section__icon--test" />
						<h3 class="pe-section__title">Servicio títulos — pruebas</h3>
					</div>
					<div class="pe-settings-grid">
						<article class="pe-panel pe-panel--test">
							<div class="pe-panel__head">
								<va-icon name="pin" class="pe-panel__icon pe-panel__icon--test" />
								<div>
									<h4 class="pe-panel__title">ID del servicio (test)</h4>
									<p class="pe-panel__desc">Identificador del servicio MET en modo pruebas.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.testMode.config.degree.services.id" placeholder="ID del servicio" class="form-input" :rules="[value => !!value || 'El ID es requerido']" />
							</div>
						</article>

						<article class="pe-panel pe-panel--test">
							<div class="pe-panel__head">
								<va-icon name="link" class="pe-panel__icon pe-panel__icon--test" />
								<div>
									<h4 class="pe-panel__title">URL del servicio (test)</h4>
									<p class="pe-panel__desc">Endpoint de conexión en ambiente de pruebas.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.testMode.config.degree.services.url" placeholder="URL del servicio" class="form-input" :rules="[value => !!value || 'La URL es requerida']" />
							</div>
						</article>

						<article class="pe-panel pe-panel--test">
							<div class="pe-panel__head">
								<va-icon name="person" class="pe-panel__icon pe-panel__icon--test" />
								<div>
									<h4 class="pe-panel__title">Usuario (test)</h4>
									<p class="pe-panel__desc">Credencial de acceso en modo pruebas.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.testMode.config.degree.services.user" placeholder="Usuario" class="form-input" :rules="[value => !!value || 'El usuario es requerido']" />
							</div>
						</article>

						<article class="pe-panel pe-panel--test">
							<div class="pe-panel__head">
								<va-icon name="key" class="pe-panel__icon pe-panel__icon--test" />
								<div>
									<h4 class="pe-panel__title">Contraseña (test)</h4>
									<p class="pe-panel__desc">Contraseña de acceso en modo pruebas.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<va-input v-model="config.testMode.config.degree.services.pass" placeholder="Contraseña" class="form-input" :rules="[value => !!value || 'La contraseña es requerida']" />
							</div>
						</article>
					</div>
				</section>
			</VaCardContent>
		</VaCard>

		<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al guardar los datos'" :test-mode="true" size="medium">
			<template #body>
				<div class="error-message">
					<div class="error-header">
						<div class="error-icon">
							<i class="fas fa-exclamation-triangle"></i>
						</div>
						<h3 class="error-title">Error</h3>
					</div>
					<p class="error-description">{{ error || 'Error al guardar los datos' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
			</template>
		</BaseModal>
		<BaseModal v-model="showSuccessModal" title="" :message="successMessage || 'Datos guardados correctamente'" :test-mode="false" size="medium">
			<template #body>
				<div class="success-message">
					<div class="success-header">
						<div class="success-icon">
							<i class="fas fa-check-circle"></i>
						</div>
						<h3 class="success-title">Éxito</h3>
					</div>
					<p class="success-description">{{ successMessage || 'Datos actualizados correctamente' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
			</template>
		</BaseModal>
	</div>
</template>

<style scoped>
.configuracion {
	--pe-navy: #0f3569;
	--pe-navy-deep: #062342;
	--pe-accent: #24c8ff;
	--pe-accent-2: #169bff;
	--pe-border: rgba(15, 53, 105, 0.12);
	--pe-surface: #ffffff;
	--pe-canvas: #f4f7fb;
	--pe-text: #0f3569;
	--pe-text-2: rgba(15, 53, 105, 0.72);
	--pe-test: #dc3545;
	--pe-radius: 16px;
	--pe-radius-sm: 12px;
	--pe-shadow: 0 8px 24px rgba(15, 53, 105, 0.08);
	width: 100%;
	max-width: 100%;
	min-width: 0;
	overflow-x: hidden;
	box-sizing: border-box;
}

.bitacora-card {
	border-radius: var(--pe-radius);
	box-shadow: var(--pe-shadow);
	animation: fadeInUp 0.5s ease-out;
	overflow: hidden;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.header-gradient {
	display: block;
	width: 100%;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 1.25rem 1.5rem;
	border-radius: 0;
	position: relative;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.header-gradient::before {
	content: '';
	position: absolute;
	inset: 0;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
	width: 100%;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	flex-wrap: wrap;
	width: 100%;
}

.header-icon-wrapper {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border: 1px solid rgba(255, 255, 255, 0.18);
}

.header-icon {
	color: white;
	font-size: 1.75rem;
}

.header-text {
	flex: 1;
	min-width: 200px;
}

.header-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: white;
	margin: 0;
	letter-spacing: -0.02em;
	text-transform: none;
	line-height: 1.2;
}

.header-subtitle {
	margin: 0.35rem 0 0;
	font-size: 0.85rem;
	color: rgba(255, 255, 255, 0.78);
	line-height: 1.45;
	max-width: 52ch;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.action-button--save {
	margin-left: auto;
	min-width: 190px;
	background: linear-gradient(90deg, var(--pe-accent-2), var(--pe-accent)) !important;
	border: none !important;
	font-weight: 600;
}

.card-content {
	padding: 1.5rem;
	background: var(--pe-canvas);
	overflow-x: hidden;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
	min-width: 0;
}

.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #e8eef5;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #9eb3cc;
	border-radius: 4px;
}

.pe-toolbar {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid var(--pe-border);
}

.pe-toolbar__meta {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
}

.pe-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.3rem 0.75rem;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 600;
	color: var(--pe-text);
	background: rgba(15, 53, 105, 0.06);
	border: 1px solid var(--pe-border);
}

.pe-chip--accent {
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.12);
	border-color: rgba(36, 200, 255, 0.28);
}

.pe-hint {
	flex-basis: 100%;
	margin: 0.35rem 0 0;
	font-size: 0.875rem;
	color: var(--pe-text-2);
	line-height: 1.5;
}

.pe-section + .pe-section {
	margin-top: 1.5rem;
	padding-top: 1.5rem;
	border-top: 1px solid var(--pe-border);
}

.pe-section__head {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 1rem;
}

.pe-section__icon {
	color: var(--pe-navy-deep);
	font-size: 1.25rem;
}

.pe-section__icon--test {
	color: var(--pe-test);
}

.pe-section__title {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 700;
	color: var(--pe-text);
	letter-spacing: -0.01em;
}

.pe-settings-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
	gap: 0.875rem;
	width: 100%;
	min-width: 0;
}

.pe-settings-grid--two {
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
}

.pe-panel {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	border-radius: var(--pe-radius-sm);
	padding: 1rem 1.1rem;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease;
}

.pe-panel:hover {
	border-color: rgba(36, 200, 255, 0.35);
	box-shadow: 0 4px 14px rgba(15, 53, 105, 0.06);
}

.pe-panel--test {
	border-color: rgba(220, 53, 69, 0.2);
}

.pe-panel--test:hover {
	border-color: rgba(220, 53, 69, 0.4);
	box-shadow: 0 4px 14px rgba(220, 53, 69, 0.08);
}

.pe-panel__head {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	margin-bottom: 0.85rem;
	padding-bottom: 0.75rem;
	border-bottom: 1px solid rgba(15, 53, 105, 0.08);
}

.pe-panel__icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 10px;
	font-size: 1.1rem;
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.1);
}

.pe-panel__icon--test {
	color: var(--pe-test);
	background: rgba(220, 53, 69, 0.1);
}

.pe-panel__title {
	margin: 0;
	font-size: 0.95rem;
	font-weight: 700;
	color: var(--pe-text);
}

.pe-panel__desc {
	margin: 0.2rem 0 0;
	font-size: 0.78rem;
	color: var(--pe-text-2);
	line-height: 1.4;
}

.pe-panel__body {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	min-width: 0;
	overflow-wrap: anywhere;
}

.pe-panel__body :deep(.va-radio) {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.5rem;
	width: 100%;
}

.pe-panel__body :deep(.va-radio__container),
.pe-panel__body :deep(.va-radio-option),
.pe-panel__body :deep(.va-radio__option) {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.45rem;
	width: 100%;
}

.pe-panel__body :deep(.va-radio__text),
.pe-panel__body :deep(.va-radio-option__label),
.pe-panel__body :deep(.va-radio__label) {
	white-space: normal;
	overflow-wrap: anywhere;
	line-height: 1.35;
}

.pe-panel__body :deep(.va-input-wrapper),
.pe-panel__body :deep(.va-input) {
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.button-icon {
	margin-right: 0.35rem;
}

.error-description,
.success-description {
	font-size: 1rem;
	line-height: 1.6;
	margin-bottom: 1rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
}

.error-description {
	border-left: 4px solid #f71d1d;
}

.success-description {
	border-left: 4px solid #28a745;
}

.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 1px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(254, 178, 178, 0.25);
	animation: fadeInUp 0.35s ease-out;
}

.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 1px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(40, 167, 69, 0.2);
	animation: fadeInUp 0.35s ease-out;
}

.error-header,
.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

.error-icon,
.success-icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.error-icon {
	background: #e67a7a;
	box-shadow: 0 4px 12px rgba(245, 101, 101, 0.35);
}

.success-icon {
	background: #1e7e34;
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.35);
}

.error-icon i,
.success-icon i {
	color: white;
	font-size: 1.35rem;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.35rem;
	font-weight: 700;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.35rem;
	font-weight: 700;
}

:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (max-width: 900px) {
	.pe-settings-grid,
	.pe-settings-grid--two {
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1rem;
	}

	.header-gradient {
		padding: 1rem;
	}

	.header-main {
		flex-direction: column;
		align-items: stretch;
	}

	.action-button--save {
		margin-left: 0;
		width: 100%;
	}

	.pe-settings-grid,
	.pe-settings-grid--two {
		grid-template-columns: 1fr;
	}
}
</style>
